import React, { useState, useRef, useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import '../styles/reportingStyles.css';

const formatYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDisplay = (ymd) => {
  if (!ymd) return '';
  const d = new Date(ymd + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'month_to_date', label: 'Month to date' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' },
  { key: 'year_to_date', label: 'Year to date' },
  { key: 'next_12_months', label: 'Next 12 months' },
  { key: 'all_time', label: 'All time' },
];

export const getPresetRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'today':
      return { start: formatYMD(today), end: formatYMD(today) };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { start: formatYMD(y), end: formatYMD(y) };
    }
    case 'this_week': {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return { start: formatYMD(weekStart), end: formatYMD(weekEnd) };
    }
    case 'last_week': {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay() - 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return { start: formatYMD(weekStart), end: formatYMD(weekEnd) };
    }
    case 'this_month': {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: formatYMD(monthStart), end: formatYMD(monthEnd) };
    }
    case 'last_month': {
      const monthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatYMD(monthStart), end: formatYMD(monthEnd) };
    }
    case 'month_to_date':
      return { start: formatYMD(new Date(today.getFullYear(), today.getMonth(), 1)), end: formatYMD(today) };
    case 'this_year':
      return { start: formatYMD(new Date(today.getFullYear(), 0, 1)), end: formatYMD(new Date(today.getFullYear(), 11, 31)) };
    case 'last_year':
      return { start: formatYMD(new Date(today.getFullYear() - 1, 0, 1)), end: formatYMD(new Date(today.getFullYear() - 1, 11, 31)) };
    case 'year_to_date':
      return { start: formatYMD(new Date(today.getFullYear(), 0, 1)), end: formatYMD(today) };
    case 'next_90_days': {
      const future = new Date(today);
      future.setDate(today.getDate() + 90);
      return { start: formatYMD(today), end: formatYMD(future) };
    }
    case 'next_12_months': {
      const future = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
      if (future.getDate() !== today.getDate()) future.setDate(0);
      return { start: formatYMD(today), end: formatYMD(future) };
    }
    case 'all_time':
      return { start: '', end: '' };
    default:
      return { start: '', end: '' };
  }
};

export const getActivePreset = (dateRange) => {
  for (const { key } of PRESETS) {
    const range = getPresetRange(key);
    if (range.start === dateRange.start && range.end === dateRange.end) return key;
  }
  return null;
};

function DateRangePicker({ dateRange, onChange, align = 'left', buttonStyle = {}, emptyLabel = 'All Time' }) {
  const [open, setOpen] = useState(false);
  const [tempRange, setTempRange] = useState(dateRange);
  const [tempPreset, setTempPreset] = useState(null);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const flatpickrRef = useRef(null);

  useEffect(() => {
    setTempRange(dateRange);
    setTempPreset(getActivePreset(dateRange));
  }, [dateRange, open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handlePresetClick = (key) => {
    setTempRange(getPresetRange(key));
    setTempPreset(key);
  };

  const handleCalendarChange = (dates) => {
    if (dates.length === 2) {
      setTempRange({ start: formatYMD(dates[0]), end: formatYMD(dates[1]) });
      setTempPreset(null);

      const fp = flatpickrRef.current;
      if (fp) {
        const startMonth = dates[0].getMonth();
        const startYear = dates[0].getFullYear();
        const endMonth = dates[1].getMonth();
        const endYear = dates[1].getFullYear();
        const monthDiff = (endYear - startYear) * 12 + (endMonth - startMonth);
        if (monthDiff >= 0 && monthDiff <= 1) {
          setTimeout(() => {
            fp.changeMonth(startMonth - fp.currentMonth + (startYear - fp.currentYear) * 12);
          }, 0);
        }
      }
    } else if (dates.length === 1) {
      setTempRange({ start: formatYMD(dates[0]), end: '' });
      setTempPreset(null);
    }
  };

  const handleApply = () => {
    onChange(tempRange);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempRange(dateRange);
    setOpen(false);
  };

  const buttonLabel = (!dateRange.start && !dateRange.end)
    ? emptyLabel
    : `${formatDisplay(dateRange.start)} – ${formatDisplay(dateRange.end)}`;

  const calendarValue = [];
  if (tempRange.start) calendarValue.push(tempRange.start);
  if (tempRange.end) calendarValue.push(tempRange.end);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-sm d-flex align-items-center"
        onClick={() => setOpen(!open)}
        style={{
          borderRadius: 6,
          padding: '4px 12px',
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          border: open ? '1.5px solid var(--sl-fg-brand, #1982c4)' : '1px solid var(--sl-border-primary, #d0d5dd)',
          background: 'var(--sl-bg-primary, #fff)',
          color: 'var(--sl-text-secondary, #344054)',
          height: 32,
          ...buttonStyle,
        }}
      >
        <i className="far fa-calendar-alt mr-2" style={{ fontSize: 13, color: 'var(--sl-text-quaternary, #667085)' }} />
        {buttonLabel}
      </button>

      {open && (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            ...(align === 'right' ? { right: 0 } : { left: 0 }),
            zIndex: 1050,
            background: 'var(--sl-bg-primary, #fff)',
            borderRadius: 12,
            boxShadow: '0 20px 24px -4px rgba(10,13,18,0.08), 0 8px 8px -4px rgba(10,13,18,0.03), 0 3px 3px -1.5px rgba(10,13,18,0.04)',
            border: '1px solid var(--sl-border-secondary, #e9eaeb)',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 620,
          }}
        >
          <div style={{ display: 'flex', flex: 1 }}>
            <div
              className="reporting-datepicker-presets"
              style={{
                width: 192,
                borderRight: '1px solid var(--sl-border-secondary, #e9eaeb)',
                padding: '12px 16px',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              {PRESETS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handlePresetClick(key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: 40,
                    textAlign: 'left',
                    padding: '8px 16px',
                    background: tempPreset === key ? 'var(--setlist-secondary-bg-hover, #fafafa)' : 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 500,
                    color: tempPreset === key ? 'var(--setlist-secondary-fg-hover, #3b3b3b)' : 'var(--sl-text-secondary, #414651)',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="reporting-datepicker-calendar" style={{ padding: '16px 20px', flex: 1 }}>
              <Flatpickr
                options={{
                  mode: 'range',
                  inline: true,
                  showMonths: 2,
                  dateFormat: 'Y-m-d',
                  monthSelectorType: 'static',
                  onReady: (selectedDates, dateStr, instance) => {
                    flatpickrRef.current = instance;
                  },
                }}
                value={calendarValue}
                onChange={handleCalendarChange}
                render={({}, ref) => <input type="hidden" ref={ref} />}
              />
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid var(--sl-border-secondary, #e9eaeb)',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <div
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--sl-border-primary, #d5d7da)',
                  borderRadius: 8,
                  fontSize: 16,
                  color: tempRange.start ? 'var(--sl-text-primary, #333)' : 'var(--sl-text-quaternary, #717680)',
                  background: 'var(--sl-bg-primary, #fff)',
                  boxShadow: '0 1px 2px rgba(10,13,18,0.05)',
                  minWidth: 136,
                  lineHeight: '24px',
                }}
              >
                {tempRange.start ? formatDisplay(tempRange.start) : 'Start date'}
              </div>
              <span style={{ color: 'var(--sl-text-quaternary, #717680)', fontSize: 16 }}>–</span>
              <div
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--sl-border-primary, #d5d7da)',
                  borderRadius: 8,
                  fontSize: 16,
                  color: tempRange.end ? 'var(--sl-text-primary, #333)' : 'var(--sl-text-quaternary, #717680)',
                  background: 'var(--sl-bg-primary, #fff)',
                  boxShadow: '0 1px 2px rgba(10,13,18,0.05)',
                  minWidth: 136,
                  lineHeight: '24px',
                }}
              >
                {tempRange.end ? formatDisplay(tempRange.end) : 'End date'}
              </div>
            </div>

            <div className="d-flex" style={{ gap: 12 }}>
              <button
                type="button"
                className="btn btn-sm btn-setlist-secondary"
                onClick={handleCancel}
                style={{ padding: '10px 14px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-setlist-primary"
                onClick={handleApply}
                style={{ padding: '10px 14px' }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
